import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * Finnhub API服务
 * 需要API密钥
 * 支持股票、外汇、加密货币
 */
class FinnhubService {
  private client = axios.create({
    baseURL: 'https://finnhub.io/api/v1',
    timeout: 10000,
  });

  // API密钥（优先级：用户配置 > 环境变量）
  private apiKey: string | null = null;

  constructor() {
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.finnhub && apiKeys.finnhub.trim() !== '') {
        this.apiKey = apiKeys.finnhub;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }
    
    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_FINNHUB_API_KEY || null;
  }

  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  async getStockPrice(symbol: string): Promise<AssetPrice> {
    if (!this.apiKey) throw new Error('Finnhub API key is required');
    
    const response = await this.client.get('/quote', {
      params: { symbol, token: this.apiKey },
    });

    const data = response.data;
    return {
      id: symbol.toLowerCase(),
      symbol: symbol.toUpperCase(),
      name: symbol.toUpperCase(),
      current_price: data.c || 0,
      price_change_24h: (data.c || 0) - (data.pc || 0),
      price_change_percentage_24h: data.dp || 0,
      last_updated: new Date().toISOString(),
      source_url: 'https://finnhub.io/api/v1',
    };
  }

  async getForexRate(from: string, to: string): Promise<AssetPrice> {
    if (!this.apiKey) throw new Error('Finnhub API key is required');
    
    const symbol = `OANDA:${from.toUpperCase()}${to.toUpperCase()}`;
    const response = await this.client.get('/quote', {
      params: { symbol, token: this.apiKey },
    });

    const data = response.data;
    return {
      id: `${from.toLowerCase()}_${to.toLowerCase()}`,
      symbol: `${from.toUpperCase()}/${to.toUpperCase()}`,
      name: `${from.toUpperCase()}/${to.toUpperCase()}`,
      current_price: data.c || 0,
      price_change_24h: (data.c || 0) - (data.pc || 0),
      price_change_percentage_24h: data.dp || 0,
      last_updated: new Date().toISOString(),
      source_url: 'https://finnhub.io/api/v1',
    };
  }

  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    if (!this.apiKey) throw new Error('Finnhub API key is required');
    
    const results: AssetPrice[] = [];
    for (const id of ids) {
      try {
        const symbol = `BINANCE:${id.toUpperCase()}USDT`;
        const response = await this.client.get('/quote', {
          params: { symbol, token: this.apiKey },
        });
        
        const data = response.data;
        results.push({
          id: id.toLowerCase(),
          symbol: id.toUpperCase(),
          name: this.getAssetName(id),
          current_price: data.c || 0,
          price_change_24h: (data.c || 0) - (data.pc || 0),
          price_change_percentage_24h: data.dp || 0,
          last_updated: new Date().toISOString(),
          source_url: 'https://finnhub.io/api/v1',
        });
      } catch (error) {
        console.warn(`Failed to get price for ${id}:`, error);
      }
    }
    return results;
  }

  private getAssetName(id: string): string {
    const map: Record<string, string> = {
      bitcoin: 'Bitcoin', btc: 'Bitcoin',
      ethereum: 'Ethereum', eth: 'Ethereum',
    };
    return map[id.toLowerCase()] || id.charAt(0).toUpperCase() + id.slice(1).toLowerCase();
  }
}

export const finnhubService = new FinnhubService();
